import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { DATE_FORMATE, STANDARD_DATE_FORMAT } from "../../constant";
import { translatePhrase } from "../../utils/functions";
import DateSelectButton from "./DateSelectButton";
import MaskInput from "../InputField/MaskInput";
import { IDateSelectInput } from "../../models/IDateInputProps";

const timeFormatter = (time: string, input_date_format?: string, output_date_format?: string) => {
	return time ? (time?.includes("/") ? time : moment(time, input_date_format).format(output_date_format)) : "";
};

const DateSelectInput = (props: IDateSelectInput) => {
	const { inputFormat = STANDARD_DATE_FORMAT, format = DATE_FORMATE } = props;
	const [value, setValue] = useState<string>(() => timeFormatter(props.value, inputFormat, DATE_FORMATE));
	const [error, setError] = useState<string>("");
	const minDate = props.minDate || new Date(1000, 0, 1).toDateString();
	useEffect(() => {
		const formattedTime = timeFormatter(props.value, inputFormat, DATE_FORMATE);
		if (formattedTime !== value) setValue(formattedTime);
		if (props.value === "") setError("");
	}, [props.value]);

	useEffect(() => {
		if (value === "") setError("");
	}, [value]);

	const handleChange = (e: any) => {
		const date: string = e?.target?.value;
		if (!date) {
			setValue("");
			props.onChange?.({
				target: {
					value: date,
					name: props.name,
					id: props.id
				}
			});
			return null;
		}
		let formated_date = date
			?.replace(/^(1)([3-9])\//g, "0$1")
			?.replace(/^([2-9])(\d)\//g, "0$1")
			?.replace(/\/(3)([2-9])\//g, "0$1")
			?.replace(/\/([4-9])(\d)\//g, "0$1");

		if (date.includes("-")) {
			formated_date = moment(e.target.value, inputFormat).format(format);
		}

		setValue(formated_date);
		isDateValid(formated_date);
		if (formated_date?.replace(/_/g, "")?.length === 10) {
			const formattedDate = moment(formated_date, format).format(format);
			props.onChange?.({
				target: {
					value: formattedDate !== "Invalid date" ? formattedDate : formated_date,
					name: props.name,
					id: props.id
				}
			});
		} else if (props.clearable) {
			props.onChange?.({
				target: {
					value: formated_date,
					name: props.name,
					id: props.id
				}
			});
		}
	};

	const isDateValid = (format: string) => {
		if (props.isBirthDay && moment(format, format).isAfter(moment().subtract(18, "years"))) {
			setError(transformPhrase("birthday_18"));
		} else if (
			isNaN(Date.parse(format)) ||
			(props.maxDate && moment(format, format).isAfter(moment(props.maxDate))) ||
			(minDate && moment(format, format).isBefore(moment(minDate)))
		) {
			setError(transformPhrase("invalidDate"));
		} else setError("");
	};
	const handleError = (err: { [name: string]: string }) => {
		const errorKey = props.id || props.name;
		if (!err[errorKey]) return props.isError?.({ [errorKey]: error });
		props.isError?.(err);
		return null;
	};
	const handleSubmit = (_date: string) => {
		let event = {
			target: {
				value: _date,
				name: props.name
			}
		};
		props.onChange(event);
		props.handleSubmit?.(_date);
	};
	const transformPhrase = (phrase: string) => {
		if (props.transformPhrase && typeof props.transformPhrase === "function" && props.UiLanguage) {
			return props.transformPhrase(phrase, props.UiLanguage, {});
		} else {
			return translatePhrase(phrase) || phrase;
		}
	};
	// calculate default month for date picker using value ( selected date ) and memoise the function to avoid re-render calculation.
	const getDefaultMonth = useMemo(() => (value ? moment(value) : moment()), [value]);

	return (
		<MaskInput
			formSubmitted={props.formSubmitted ?? false}
			format="##/##/####"
			id={props.id ?? ""}
			isError={handleError}
			label={props.label ?? ""}
			mask="_"
			name={props.name}
			onBlur={props.onBlur}
			onChange={handleChange}
			onContextMenu={props.onContextMenu}
			onCopy={props.onCopy}
			onCut={props.onCut}
			onFocus={props.onFocus}
			placeholder={props.placeholder ?? DATE_FORMATE}
			value={value}
			className={props.className ?? ""}
			required={props.required ?? false}
			sectionType="right"
			sectionRightIcon={
				<DateSelectButton
					handleCancel={props.handleCancel}
					handleSubmit={handleSubmit}
					inputFormat={DATE_FORMATE}
					format={DATE_FORMATE}
					defaultMonth={props.defaultMonth ?? getDefaultMonth}
					value={value}
					disableHeader={props.disableHeader}
					disableFooter={props.disableFooter}
					size={props.size}
				/>
			}
		/>
	);
};

DateSelectInput.displayName = "DateSelectInput";
export default DateSelectInput;
